import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import debounce from "lodash.debounce";
import {
  setSearchQuery,
  setSelectedSubCategory,
  setSearchText,
  setSelectedItem,
  setCurrentPage,
  setRadius,
  setSearchTerm,
} from "../../../redux/slices/LcaSlice";
import {
  RiFolderFill,
  RiBriefcaseFill,
  RiHome4Fill,
  RiStoreFill,
  RiHeartFill,
  RiStarFill,
  RiCalendarFill,
  RiAdvertisementFill,
  RiTShirtFill,
  RiCarFill,
  RiPlayCircleFill,
  RiMicFill,
  RiUserLocationFill,
  RiTentFill,
  RiHeadphoneFill,
  RiTreasureMapFill,
  RiBrushFill,
  RiGovernmentFill,
} from "react-icons/ri";
import { fetchCategories, fetchSubCategories, postRequest } from "../Data";
import axiosClient from "../../../axios-client";
import "./searchLca.css";
import {
  checkExisting,
  setActionType,
  setInquiryNeedOpen,
} from "../../../redux/slices/SearchBarSlice";
import { setEngagedCouple } from "../../../redux/slices/RequestFormSlice";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { PopulateInquiry } from "../LocalCummunityAds/PopulateInquiry";

// Icon helpers
const getIconForName = (name) => {
  if (!name) return <RiFolderFill />;
  const n = name.toLowerCase();
  if (n === "job market") return <RiBriefcaseFill />;
  if (n === "real estate") return <RiHome4Fill />;
  if (n === "local business ads") return <RiStoreFill />;
  if (n === "highlight") return <RiStarFill />;
  if (n === "matrimony") return <RiHeartFill />;
  if (n === "event businesses & customers") return <RiCalendarFill />;
  return <RiFolderFill />;
};

const getVendorIcon = (name) => {
  if (!name) return <RiFolderFill />;
  const n = name.toLowerCase();
  if (n.includes("photography")) return <RiStarFill />;
  if (n.includes("venue")) return <RiHome4Fill />;
  if (n.includes("catering") || n.includes("cuisine")) return <RiStoreFill />;
  if (n.includes("decoration")) return <RiHeartFill />;
  if (n.includes("music")) return <RiCalendarFill />;
  if (n.includes("ads")) return <RiAdvertisementFill />;
  if (n.includes("clothing")) return <RiTShirtFill />;
  if (n.includes("transportation")) return <RiCarFill />;
  if (n.includes("dance")) return <RiPlayCircleFill />;
  if (n.includes("mc/host")) return <RiMicFill />;
  if (n.includes("turban")) return <RiUserLocationFill />;
  if (n.includes("tent")) return <RiTentFill />;
  if (n.includes("planning")) return <RiCalendarFill />;
  if (n.includes("dj")) return <RiHeadphoneFill />;
  if (n.includes("jewelry")) return <RiTreasureMapFill />;
  if (n.includes("hair") || n.includes("makeup")) return <RiBrushFill />;
  if (n.includes("mehndi")) return <RiBrushFill />;
  if (n.includes("gurudwara") || n.includes("temple"))
    return <RiGovernmentFill />;
  return <RiFolderFill />;
};

const fetchSuggestion = async (query) => {
  try {
    const res = await axiosClient.get(`services/searchSuggetion/${query}`);
    if (res.data.statusCode === 200) return res.data.Data || [];
    return [];
  } catch (err) {
    console.error("Error fetching suggestions:", err);
    return [];
  }
};

const SearchLca = () => {
  const userinfo = useSelector((state) => state.user.userInfo);
  const islogin = useSelector((state) => state.user.isLogin);
  const user = islogin ? JSON.parse(userinfo) : null;
  const dispatch = useDispatch();
  const isAdmin = user && user.Role === "admin";
  const isVendor = user?.Role === "provider";
  const { searchQuery, searchTerm } = useSelector((state) => state.lcAds || {});
  const [adsCategories, setAdsCategories] = useState([]);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const [zipSuggestions, setZipSuggestions] = useState([]);
  const [zipLoading, setZipLoading] = useState(false);
  const [displayValue, setDisplayValue] = useState(""); // For input display

  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef(null);
  const API_KEY = "b8781580fead40ca9de977a25ea50d16";

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setActiveInput(null);
        setZipSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load categories
  useEffect(() => {
    const loadData = async () => {
      try {
        const [category, subCategories] = await Promise.all([
          fetchCategories(),
          fetchSubCategories(),
        ]);
        const filterCategoryByName = category.filter((cat) => cat.Id === 1);
        const filterSubCat = subCategories.filter(
          (subCat) => subCat.CategoryId === filterCategoryByName[0]?.Id
        );
        const safeSubCats = Array.isArray(filterSubCat) ? filterSubCat : [];
        setAdsCategories(safeSubCats.slice(0, 6));
        setVendorCategories(Array.isArray(category) ? category : []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setAdsCategories([]);
        setVendorCategories([]);
      }
    };
    loadData();
  }, []);

  // Fetch zip suggestions
  useEffect(() => {
    const fetchZipSuggestions = async () => {
      if (!searchTerm || searchTerm.length < 3) {
        setZipSuggestions([]);
        return;
      }
      setZipLoading(true);
      try {
        const res = await axios.get(
          `https://api.geoapify.com/v1/geocode/autocomplete`,
          {
            params: {
              text: searchTerm,
              apiKey: API_KEY,
              type: "postcode",
              filter: "countrycode:us",
            },
          }
        );

        const options = res.data.features
          .map((f) => {
            const p = f.properties;
            return {
              postcode: p.postcode?.split("-")[0],
              city: p.city,
              state: p.state,
              display: `${p.postcode?.split("-")[0]} - ${p.city}, ${p.state}`,
            };
          })
          .filter(
            (v, i, a) => a.findIndex((t) => t.postcode === v.postcode) === i
          );

        setZipSuggestions(options);
      } catch (err) {
        console.error("Geoapify error:", err);
      } finally {
        setZipLoading(false);
      }
    };

    const delay = setTimeout(fetchZipSuggestions, 400);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  // Sync display value when searchTerm changes (from external source)
  useEffect(() => {
    if (!searchTerm) {
      setDisplayValue("");
    }
  }, [searchTerm]);

  // Category filtering
  const uniqueAdsCategories = Array.from(
    new Map(
      adsCategories.map((cat) => [cat.subCatName || cat.Name, cat])
    ).values()
  );

  const filterCategories = (query, categories) => {
    if (!query || query.length < 3) return categories;
    const q = query.toLowerCase();
    return categories.filter((cat) => {
      const label = (cat.subCatName || cat.Name || "").toLowerCase();
      return label.includes(q);
    });
  };

  const filteredAdsCategories = filterCategories(
    searchQuery || "",
    uniqueAdsCategories
  );
  const filteredVendorCategories = filterCategories(
    searchQuery || "",
    vendorCategories
  );

  // Debounced search suggestions
  const debouncedFetchSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length >= 3) {
        setIsFetchingSuggestions(true);
        const data = await fetchSuggestion(query);
        setSuggestions(data);
        setIsFetchingSuggestions(false);
      } else {
        setSuggestions([]);
        setIsFetchingSuggestions(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (searchQuery) debouncedFetchSuggestions(searchQuery);
    else setSuggestions([]);
    return () => debouncedFetchSuggestions.cancel();
  }, [searchQuery, debouncedFetchSuggestions]);

  // URL sync
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromUrl = params.get("category");
    if (categoryFromUrl) {
      dispatch(setSearchQuery(categoryFromUrl));
      dispatch(setSearchText(categoryFromUrl));
    }
  }, [location.search, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromUrl = params.get("category");
    if (!categoryFromUrl) {
      dispatch(setSearchQuery(""));
      dispatch(setSearchText(""));
    }
  }, [location.pathname, dispatch]);

  // Updated submitRequst to accept current search term
  const submitRequst = async (currentSearchQuery) => {
    if (!currentSearchQuery || currentSearchQuery.trim() === "") {
      return; // Prevent empty title
    }
    if (isAdmin || isVendor || !islogin) return;
    let needExists = false;
    let enquiryData = null;
    let enquiryStep = null;
    if (islogin) {
      const checkForm = {
        Title: currentSearchQuery.trim() + " (Auto Generated)",
        userId: user.UserID,
      };
      const result = await dispatch(checkExisting(checkForm)).unwrap();
      needExists = result?.data;
      enquiryData = result?.dataStep;
      enquiryStep = result?.dataStep?.enquiryStep;
    }
    if (needExists) return;
    if (enquiryStep > 0) {
      PopulateInquiry(enquiryData, dispatch);
    }
    if (enquiryData && enquiryStep >= 4) {
      const final = {
        ...enquiryData,
        otherVendors: [],
        eventDetails: null,
        engagedCouple: 0,
        enquiryStep: undefined,
        Title: currentSearchQuery.trim() + " (Auto Generated)",
      };
      await postRequest(final);
    } else {
      dispatch(setInquiryNeedOpen(true));
      dispatch(setActionType("SEARCH"));
    }
  };

  // Handlers - now pass the latest value explicitly
  const handleCategorySelect = async (selected) => {
    dispatch(setSearchQuery(selected));
    dispatch(setSearchText(selected));
    setSuggestions([]);
    setActiveInput(null);
    dispatch(setCurrentPage(1));
    await submitRequst(selected); // Pass selected value directly
    const params = new URLSearchParams(location.search);
    params.set("category", selected);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleFind = async () => {
    if (!searchQuery) return;
    dispatch(setCurrentPage(1));
    dispatch(setSearchText(searchQuery));
    setActiveInput(null);
    await submitRequst(searchQuery); // Use current searchQuery safely
  };

  const handleZipSelect = (suggestion) => {
    dispatch(setSearchTerm(suggestion.postcode));
    setDisplayValue(suggestion.display);
    setZipSuggestions([]);
    setActiveInput(null);
  };

  const handleZipInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    dispatch(setSearchTerm(value));
    dispatch(setRadius(15));
    setDisplayValue(value);
  };

  const clearZip = () => {
    dispatch(setSearchTerm(""));
    dispatch(setRadius(15));
    setDisplayValue("");
    setZipSuggestions([]);
  };

  return (
    <div className="searchLca-bar-container" ref={wrapperRef}>
      <div className="search-bar">
        <div className="split-input">
          {/* Category Input */}
          <div className="category-input-wrapper">
            <div className="input-with-icon">
              <SearchIcon className="search-icon" />
              <input
                type="text"
                className="split-part"
                placeholder="Search"
                value={searchQuery || ""}
                onChange={(e) => {
                  dispatch(setSearchQuery(e.target.value));
                  setActiveInput("category");
                }}
                onFocus={() => setActiveInput("category")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleFind();
                }}
              />
              {searchQuery && (
                <span
                  className="clear-btn"
                  onClick={() => {
                    dispatch(setSearchQuery(""));
                    dispatch(setSearchText(""));
                    setSuggestions([]);
                    setActiveInput(null);
                    const params = new URLSearchParams(location.search);
                    params.delete("category");
                    dispatch(setSelectedSubCategory(null));
                    dispatch(setSelectedItem(null));
                    params.delete("subcategory");
                    params.delete("item");
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                >
                  x
                </span>
              )}
            </div>
          </div>

          <div className="divider" />

          {/* Zipcode Input */}
          <div
            className="location-input-wrapper"
            style={{ position: "relative" }}
          >
            <div className="input-with-icon">
              <LocationOnIcon className="search-icon" />
              <input
                className="split-part"
                placeholder="Search By Zipcode..."
                value={displayValue}
                onChange={handleZipInputChange}
                onFocus={() => setActiveInput(null)}
                onKeyDown={(e) => {
                  if ((e.keyCode < 48 || e.keyCode > 57) && e.keyCode !== 8) {
                    e.preventDefault();
                  }
                }}
              />
              {displayValue && (
                <span className="clear-btn" onClick={clearZip}>
                  x
                </span>
              )}
            </div>

            {/* Loading */}
            {zipLoading && (
              <div style={{ fontSize: 12, marginTop: 4 }}>Loading...</div>
            )}

            {/* Zip Suggestions Dropdown */}
            {zipSuggestions.length > 0 && (
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "4px 0 0",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  background: "#fff",
                  position: "absolute",
                  width: "100%",
                  zIndex: 100,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                {zipSuggestions.map((s, i) => (
                  <li
                    key={i}
                    onClick={() => handleZipSelect(s)}
                    style={{
                      padding: "8px 10px",
                      cursor: "pointer",
                      borderBottom:
                        i < zipSuggestions.length - 1
                          ? "1px solid #eee"
                          : "none",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#fff")
                    }
                  >
                    {s.display}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <button className="search-btn" onClick={handleFind}>
          Search
        </button>
        {/* Category Dropdown */}
        {activeInput === "category" && (
          <ul className="cat-dropdown category-dropdown">
            <div className="dropdown-wrapper">
              {isFetchingSuggestions ? (
                <li className="no-results">Loading...</li>
              ) : suggestions.length > 0 ? (
                <div className="dropdown-section">
                  <h6>Suggestions</h6>
                  {suggestions.map((s, i) => (
                    <li
                      key={`sugg-${i}`}
                      onClick={() => handleCategorySelect(s.Title)}
                    >
                      <SearchIcon fontSize="small" style={{ marginRight: 6 }} />
                      {s.Title}
                    </li>
                  ))}
                  <hr className="divider" />
                </div>
              ) : null}

              {(filteredVendorCategories.length > 0 ||
                filteredAdsCategories.length > 0) && (
                <div className="dropdown-sections">
                  {filteredVendorCategories.length > 0 && (
                    <div className="dropdown-section left-section">
                      <h4>Vendor Categories</h4>
                      {filteredVendorCategories.map((cat, i) => (
                        <li
                          key={cat.Id || i}
                          onClick={() => handleCategorySelect(cat.Name)}
                        >
                          <span className="cat-icon">
                            {getVendorIcon(cat.Name)}
                          </span>
                          {cat.Name}
                        </li>
                      ))}
                    </div>
                  )}

                  {filteredVendorCategories.length > 0 &&
                    filteredAdsCategories.length > 0 && (
                      <div className="vertical-divider-searchbar" />
                    )}

                  {filteredAdsCategories.length > 0 && (
                    <div className="dropdown-section right-section">
                      <h4>Ads Categories</h4>
                      {filteredAdsCategories.map((cat, i) => {
                        const label = cat.subCatName || cat.Name || "Unnamed";
                        return (
                          <li
                            key={cat.Id || i}
                            onClick={() => handleCategorySelect(label)}
                          >
                            <span className="cat-icon">
                              {getIconForName(label)}
                            </span>
                            {label}
                          </li>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </ul>
        )}
      </div>
    </div>
  );
};

export default SearchLca;
